<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, onBeforeMount, onBeforeUnmount, ref } from "vue"
import { onBeforeRouteLeave } from "vue-router"
import { useI18n } from "vue-i18n"

import type { BreadcrumbItem } from "@core/components/layout/header/HeaderPage.vue"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { unregisterEnergyManagerMessages } from "@modules/energy-manager/utils/i18n"
import { useEventBus } from "@vueuse/core"
import { get } from "lodash-es"

// ================================
// TYPES & INTERFACES
// ================================
type UpdateFetchBuildingsState = {
    total?: number
    page?: number
    limit?: number
}

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            breadcrumbAccueil: "Accueil",
            breadcrumbPageTitle: "Tableau de bord global",
        },
    },
})

// ================================
// STORES
// ================================
/**
 * Store Pinia pour gérer l'état du tableau de bord Energy Manager.
 */
const emConsoCompareStore = useEmConsoCompareStore()

// ================================
// CONSTANTES
// ================================
/**
 * Configuration des blocs du squelette affiché pendant le chargement des données.
 */
const CONFIG_SKELETON = [
    ["100%", "2.5rem"],
    ["100%", "2.5rem"],
    ["100%", "46.6rem"],
    ["100%", "15.6rem"],
    ["100%", "37.2rem"],
    ["100%", "14.6rem"],
] as const

// ================================
// ÉTAT DES DONNÉES
// ================================
/**
 * Informations sur l'état du chargement des bâtiments via le bus d'événements.
 */
const updateFetchBuildings = ref<UpdateFetchBuildingsState>({
    total: undefined,
    page: undefined,
    limit: undefined,
})

// ================================
// ÉVÉNEMENTS
// ================================
const bus = useEventBus<string>("update:buildings")

/**
 * Met à jour les informations de chargement après réception d'un événement du bus.
 * @param eventPayload - Charge utile JSON contenant les informations de pagination.
 */
function onUpdateBuildings(eventPayload: string): void {
    const parsedPayload = JSON.parse(eventPayload)
    const total = get(parsedPayload, "total", undefined)
    const page = get(parsedPayload, "page", undefined)
    const limit = get(parsedPayload, "limit", undefined)

    updateFetchBuildings.value = {
        total,
        page,
        limit,
    }
}

const unsubscribeUpdateBuildings = bus.on(onUpdateBuildings)

// ================================
// DONNÉES CALCULÉES
// ================================
/**
 * Fil d'Ariane (Accueil → titre de la page).
 */
const getBreadcrumb = computed<BreadcrumbItem[]>(() => [
    {
        icon: "pi pi-home",
        label: t("breadcrumbAccueil"),
        route: "/",
    },
    { icon: "pi pi-chart-bar", label: t("breadcrumbPageTitle") },
])

/**
 * Détermine le mode d'affichage de la barre de progression en fonction de la disponibilité des données.
 */
const getProgressBarMode = computed(() => {
    if (updateFetchBuildings.value.total === undefined) {
        return "indeterminate"
    }

    return "determinate"
})

/**
 * Calcule le nombre de bâtiments déjà chargés à partir des informations de pagination.
 */
const getLoadedBuildingsCount = computed(() => {
    if (updateFetchBuildings.value.total === undefined) {
        return 0
    }

    const totalLoaded =
        (updateFetchBuildings.value.page ?? 0) *
        (updateFetchBuildings.value.limit ?? 0)

    if (totalLoaded > updateFetchBuildings.value.total!) {
        return updateFetchBuildings.value.total!
    }

    return totalLoaded
})

/**
 * Détermine le pourcentage de bâtiments chargés par rapport au total.
 */
const getLoadedBuildingsPercent = computed(() => {
    if (
        updateFetchBuildings.value.total === undefined ||
        updateFetchBuildings.value.total === 0
    ) {
        return 0
    }

    return Math.round(
        (getLoadedBuildingsCount.value / updateFetchBuildings.value.total!) *
            100
    )
})

// ================================
// LOGIQUE MÉTIER
// ================================
/**
 * Initialise le chargement des données nécessaires à l'affichage du tableau de bord.
 */
onBeforeMount(() => {
    emConsoCompareStore.fetchBuildingsAndFolders.fetch()
    emConsoCompareStore.fetchTags.fetch()
})

/**
 * Nettoie l'abonnement au bus d'événements avant la destruction du composant.
 */
onBeforeUnmount(() => {
    unsubscribeUpdateBuildings()
})

onBeforeRouteLeave(() => {
    unregisterEnergyManagerMessages()
})
</script>
<template>
    <!-- ================================ -->
    <!-- PAGE DASHBOARD ENERGY MANAGER -->
    <!-- ================================ -->
    <div
        class="page dashboard-page dashboard-page--skeleton relative flex min-h-0 flex-1 flex-col gap-8 p-6"
        v-if="emConsoCompareStore.fetchBuildingsAndFolders.isFetching"
    >
        <!-- ================================ -->
        <!-- EN-TÊTE (HeaderPage via DashboardHeader) -->
        <!-- ================================ -->
        <DashboardHeader
            :breadcrumb="getBreadcrumb"
            :show-helper-message="false"
        />

        <!-- ================================ -->
        <!-- BARRE DE PROGRESSION DE CHARGEMENT -->
        <!-- ================================ -->
        <div
            class="dashboard-page__progress-bar-wrapper absolute left-1/2 top-[36rem] z-[1000] flex w-full -translate-x-1/2 flex-col gap-6"
        >
            <BaseProgressBar
                class="dashboard-page__progress-bar mx-auto h-4 w-4/5"
                :mode="getProgressBarMode"
                :value="getLoadedBuildingsPercent"
            />
            <p
                class="dashboard-page__progress-bar-text text-center"
                v-if="updateFetchBuildings.total"
            >
                Chargement de
                <span class="font-bold">{{ getLoadedBuildingsCount }}</span>
                bâtiments sur
                <span class="font-bold">{{ updateFetchBuildings.total }}</span>
                en cours...
            </p>
            <p v-else class="dashboard-page__progress-bar-text text-center">
                En cours de chargement des bâtiments...
            </p>
        </div>

        <!-- ================================ -->
        <!-- BLOCS SQUELETTE -->
        <!-- ================================ -->
        <BaseSkeleton
            v-for="(config, index) in CONFIG_SKELETON"
            :key="index"
            :width="config[0]"
            :height="config[1]"
        />
    </div>

    <div
        v-else
        class="page dashboard-page relative flex min-h-0 flex-1 flex-col gap-8 p-6"
    >
        <!-- ================================ -->
        <!-- EN-TÊTE + APERÇU GLOBAL -->
        <!-- ================================ -->
        <DashboardHeader :breadcrumb="getBreadcrumb" />

        <!-- ================================ -->
        <!-- CARTE DE COMPARAISON -->
        <!-- ================================ -->
        <DashboardCardCompare />

        <!-- ================================ -->
        <!-- CARTE TOP / FLOP -->
        <!-- ================================ -->
        <DashboardCardTopFlop />

        <!-- ================================ -->
        <!-- LISTE DES BÂTIMENTS -->
        <!-- ================================ -->
        <DashboardCardBuilding />
    </div>
</template>
